import { Button, Card, CircularProgress, Grid } from '@material-ui/core'
import {
  Add,
  AttachMoney,
  Bookmark,
  Edit,
  Event,
  HourglassEmpty,
  Person,
  Room,
} from '@material-ui/icons'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import {
  getProjectDetails,
  submitQuote,
  updateProject,
  updateQuote,
} from '../../../actions/projectActions'
import { listAdminUsers } from '../../../actions/userActions'
import customCircleCheck from '../../../assets/img/icons/ic-check-circle.png'
import locationsList from '../../../data/locations'
import { arraysEqual } from '../../../helpers/Helpers'
import { useDateFormatter } from '../../../hooks/useDateFormatter'
import { useHover } from '../../../hooks/useHover'
import { useInput } from '../../../hooks/useInput'
import AutoCompleteSelect from '../../Form/AutoCompleteSelect'
import Datepicker from '../../Form/Datepicker'
import NumberInput from '../../Form/NumberInput'
import SingleFileDisplay from '../../Form/SingleFileDisplay'
import SingleFileInput from '../../Form/SingleFileInput'
import ModalPopUp from '../../ModalPopUp/ModalPopUp'
import AEAddPODocs from './AEAddPODocs'
import ProjectDeliverables from './ProjectDeliverables'
import ReviewPurchaseOrder from './ReviewPurchaseOrder'
import RevisionsModal from './RevisionsModal'

let signaturePoll

const HoverButton = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  color: var(--color-primary-hover);

  svg {
    width: 14px;
    margin-right: 5px;
  }
`

const ProjectActionsAdmin = () => {
  const dispatch = useDispatch()

  const [hoverRef, isHovered] = useHover()
  const [dataIsSet, setDataIsSet] = useState(false)
  const [showLocation, setShowLocation] = useState(false)
  const [showAssignees, setShowAssignees] = useState(false)
  const [showDueDate, setShowDueDate] = useState(false)
  const [showPrice, setShowPrice] = useState(false)
  const [dueDate, setDueDate] = useState(null)
  const [acceptanceCriteria, setAcceptanceCriteria] = useState({})
  const [deliverables, setDeliverables] = useState([])
  const [showAcceptanceCriteria, setShowAcceptanceCriteria] = useState(true)
  const [submitIsReady, setSubmitIsReady] = useState(false)
  const [showAddRevision, setShowAddRevision] = useState(false)
  const [showRevisionsModal, setShowRevisionsModal] = useState(false)
  const [previousUpdatedAt, setPreviousUpdatedAt] = useState('')
  const [showAddPODocsModal, setShowAddPODocsModal] = useState(false)
  const {
    value: location,
    setValue: setLocation,
    error: locationError,
  } = useInput('')
  const {
    value: assignee,
    setValue: setAssignee,
    error: assigneeError,
  } = useInput('')
  const {
    value: price,
    setValue: setPrice,
    error: priceError,
  } = useInput(undefined)

  const { userInfo } = useSelector((state) => state.userLogin)
  const { users: adminList } = useSelector((state) => state.userAdminList)
  const { exit } = useSelector((state) => state.projectExit)
  const { project } = useSelector((state) => state.projectDetails)
  const { success: successUpdate } = useSelector((state) => state.projectUpdate)

  const readableDueDate = useDateFormatter(project.dueDate, 'MM-DD-YYYY')

  const documentIsSigned =
    project.acceptanceCriteria &&
    project.acceptanceCriteria.length > 0 &&
    project.acceptanceCriteria[project.acceptanceCriteria.length - 1].isSigned
      ? true
      : false

  const updatedAt = useDateFormatter(
    acceptanceCriteria ? acceptanceCriteria.updatedAt : ''
  )

  const signatureProcessing =
    project.acceptanceCriteria &&
    project.acceptanceCriteria.length > 0 &&
    project.acceptanceCriteria[project.acceptanceCriteria.length - 1]
      .signatureProcessing

  useEffect(() => {
    // if we have a signature processing
    if (
      project.acceptanceCriteria &&
      project.acceptanceCriteria.length > 0 &&
      project.acceptanceCriteria[project.acceptanceCriteria.length - 1]
        .signatureProcessing
    ) {
      clearTimeout(signaturePoll)

      signaturePoll = setTimeout(() => {
        dispatch(getProjectDetails(project._id))
      }, 15 * 1000) // Poll every 15 seconds to see if there are updates
    } else {
      if (signaturePoll) {
        clearTimeout(signaturePoll)
        signaturePoll = null
      }
    }
    return () => {
      clearTimeout(signaturePoll)
      setDataIsSet(false)
    }
  }, [dispatch, project])

  useEffect(() => {
    dispatch(listAdminUsers())
  }, [dispatch])

  useEffect(() => {
    let ready = false

    // Check if fields missing
    if (
      (price !== '' || (project.charges && project.charges.length > 0)) &&
      (Object.keys(acceptanceCriteria).length !== 0 ||
        (project.acceptanceCriteria && project.acceptanceCriteria.length > 0))
    ) {
      // Check if fields dirty
      if (
        location !== project.location ||
        assignee !== project.assignee ||
        dueDate !== project.dueDate ||
        price !== project.charges[project.charges.length - 1] ||
        !project.charges.length ||
        !arraysEqual(acceptanceCriteria, project.acceptanceCriteria) ||
        !project.acceptanceCriteria
      ) {
        ready = true
      }
    }

    setSubmitIsReady(ready)
  }, [location, assignee, dueDate, price, acceptanceCriteria, project])

  useEffect(() => {
    if (successUpdate) {
      setShowAddRevision(false)
    }

    if (exit) {
      submitHandler()
    }

    if (project) {
      // Resetting deliverables so <ProjectDeliverables/> can make updated comparison
      setDeliverables(project.deliverables)
    }

    if (project && !dataIsSet) {
      setDataIsSet(true)

      setLocation(project.location)
      setAssignee(project.assignee)
      setDueDate(project.dueDate)
      setPrice(
        project.charges && project.charges.length > 0
          ? project.charges[project.charges.length - 1].price
          : undefined
      )
      setAcceptanceCriteria(
        project.acceptanceCriteria && project.acceptanceCriteria.length > 0
          ? project.acceptanceCriteria[project.acceptanceCriteria.length - 1]
          : {}
      )
      setDeliverables(project.deliverables)
      setSubmitIsReady(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    adminList,
    project,
    setLocation,
    setAssignee,
    setDueDate,
    dataIsSet,
    setPrice,
    setAcceptanceCriteria,
    exit,
    acceptanceCriteria,
    price,
    location,
    assignee,
    dueDate,
    dispatch,
    successUpdate,
  ])

  const submitHandler = () => {
    let projectClone = { ...project }
    let charges = [...project.charges]
    let acDocs = [...project.acceptanceCriteria]
    let priceOrACupdated = false

    if (Object.keys(acceptanceCriteria).length !== 0) {
      if (acDocs.length) {
        // If the last document is signed, add the document to the array
        // Otherwise, update the value
        if (acDocs[acDocs.length - 1].isSigned) {
          // If it is not the same document...
          if (acDocs[acDocs.length - 1].id !== acceptanceCriteria.id) {
            acDocs.push(acceptanceCriteria)
            priceOrACupdated = true
          }
        } else {
          acDocs[acDocs.length - 1] = { ...acceptanceCriteria }
        }
      } else {
        acDocs.push(acceptanceCriteria)
      }
    } else {
      // Need to remove service agreement,
      // otherwise AE is unable to delete existing service agreement
      acDocs = []
    }

    if (price) {
      if (charges.length > 0) {
        // If the last charge is paid, add the charge to the array
        // Otherwise, update the value
        if (charges[charges.length - 1].isPaid) {
          if (showPrice && price > 0) {
            charges.push({ price })
            priceOrACupdated = true
          }
        } else {
          charges[charges.length - 1].price = price
        }
      } else {
        charges.push({ price })
      }
    }

    projectClone.assignee = assignee
    projectClone.location = location
    projectClone.dueDate = dueDate
    projectClone.charges = charges
    projectClone.acceptanceCriteria = acDocs
    projectClone.deliverables = deliverables

    if (project.status === 'Submitted for review' && !exit) {
      projectClone.status = 'Quote submitted'
      projectClone.event = {
        event: 'Quote submitted',
        user: userInfo._id,
      }

      dispatch(submitQuote(projectClone))
    } else if (priceOrACupdated) {
      projectClone.status = 'Quote submitted'
      projectClone.event = {
        event: 'Quote updated',
        user: userInfo._id,
      }
      dispatch(updateQuote(projectClone))
      setShowAddRevision(false)
    } else {
      dispatch(updateProject(projectClone))
    }

    setShowLocation(false)
    setShowAssignees(false)
    setShowDueDate(false)
    setShowPrice(false)
  }

  const handleRemoveClick = () => {
    setShowAcceptanceCriteria(true)
    setAcceptanceCriteria('')
  }

  const handleShowAddRevision = () => {
    setPreviousUpdatedAt(updatedAt)
    setShowAddRevision(true)
    setAcceptanceCriteria('')
  }

  const handleShowPrice = () => {
    if (project.charges.length) {
      if (project.charges[project.charges.length - 1].isPaid) {
        setPrice('')
      }
    }

    setShowPrice(true)
  }

  const handleAddPOClick = () => {
    setShowAddPODocsModal(true)
  }

  return (
    <form className="form-container" onSubmit={submitHandler}>
      {project.status === 'Submitted for review' && (
        <>
          <h2 className="primary-text">Submit quote</h2>
          <p>
            Complete details below, then hit ‘submit’ for the customer to
            proceed with the quote.
          </p>
        </>
      )}
      <div className="quote-details">
        <div className="flex space-between align-center">
          <h4>Quote details</h4>

          {project.status !== 'Submitted for review' && (
            <Button
              variant="contained"
              color="primary"
              onClick={submitHandler}
              disabled={!submitIsReady || project.status === 'Archived'}
            >
              Update
            </Button>
          )}
        </div>
        <Grid container spacing={0} className="input-row m-t-15">
          <Grid item xs={4} className="label-item">
            <Room fontSize="small" />
            <h5>Location</h5>
          </Grid>

          <Grid item xs={8} className="input-item">
            {showLocation ? (
              <AutoCompleteSelect
                name="location"
                options={locationsList}
                value={location}
                setValue={setLocation}
                error={locationError}
                minWidth={242}
              />
            ) : (
              <Button color="primary" onClick={() => setShowLocation(true)}>
                {project.location ? (
                  project.location
                ) : (
                  <>
                    <Add className="m-r-5" />
                    Set Location
                  </>
                )}
              </Button>
            )}
          </Grid>
        </Grid>

        <Grid container spacing={0} className="input-row">
          <Grid item xs={4} className="label-item">
            <Person fontSize="small" />
            <h5>Assignee</h5>
          </Grid>
          <Grid item xs={8} className="input-item">
            {showAssignees ? (
              <AutoCompleteSelect
                name="assignee"
                options={adminList.map((x) => ({
                  value: x._id,
                  label: x.firstName + ' ' + x.lastName,
                }))}
                value={assignee}
                setValue={setAssignee}
                error={assigneeError}
                minWidth={242}
              />
            ) : (
              <Button color="primary" onClick={() => setShowAssignees(true)}>
                {project.assignee ? (
                  project.assigneeName
                ) : (
                  <>
                    <Add className="m-r-5" />
                    Set Assignee
                  </>
                )}
              </Button>
            )}
          </Grid>
        </Grid>

        <Grid container spacing={0} className="input-row">
          <Grid item xs={4} className="label-item">
            <Event fontSize="small" />
            <h5>Due date</h5>
          </Grid>

          <Grid item xs={8} className="input-item">
            {showDueDate ? (
              <Datepicker
                value={dueDate}
                setValue={setDueDate}
                className="m-t-0 m-b-0"
              />
            ) : (
              <Button color="primary" onClick={() => setShowDueDate(true)}>
                {project.dueDate ? (
                  readableDueDate
                ) : (
                  <>
                    <Add className="m-r-5" />
                    Set due date
                  </>
                )}
              </Button>
            )}
          </Grid>
        </Grid>

        <Grid
          container
          spacing={0}
          className={`input-row ${
            project.charges && project.charges.length > 0
              ? 'no-height m-t-16'
              : ''
          }`}
        >
          <Grid item xs={4} className="label-item">
            <div className="flex">
              <AttachMoney fontSize="small" />
              <h5>Price</h5>
            </div>
          </Grid>
          <Grid item xs={8} className="input-item">
            {showPrice ? (
              <NumberInput
                value={price}
                setValue={setPrice}
                error={priceError}
              />
            ) : (
              <Button color="primary" onClick={handleShowPrice}>
                {project.price ? (
                  project.price.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })
                ) : (
                  <>
                    <Add className="m-r-5" />
                    {project.charges &&
                    project.charges.length > 0 &&
                    project.charges[project.charges.length - 1].isPaid
                      ? 'Add charge'
                      : 'Set price'}
                  </>
                )}
              </Button>
            )}
          </Grid>
          {project.charges && project.charges.length > 0 && (
            <Grid item xs={12} className="m-t-10 m-b-16 p-r-16">
              <div className="flex column-reverse">
                {project.charges && project.charges.length > 1 && (
                  <>
                    <div className="align-center flex gray-text space-between">
                      <div className="fs14 flex">
                        <div className="m-r-10" style={{ width: 60 }}>
                          Total:
                        </div>
                        {project.charges
                          .reduce((acc, cur) => acc + cur.price, 0)
                          .toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                          })}
                      </div>
                    </div>
                    <div
                      className="gradient-bg m-t-5 m-b-5"
                      style={{ height: 2, width: 160 }}
                    ></div>
                  </>
                )}

                {project.charges &&
                  project.charges.length > 0 &&
                  project.charges.map((x, index) => (
                    <div
                      key={index}
                      className="align-center flex gray-text space-between"
                    >
                      <div className="fs14 flex">
                        <div className="m-r-10" style={{ width: 60 }}>
                          {index === 0 ? 'Initial' : 'Added'}:
                        </div>
                        {x.price.toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'USD',
                        })}
                      </div>
                      <div className="fs14">
                        {x.isPaid ? (
                          <div className="paid">
                            <img
                              src={customCircleCheck}
                              alt="Circle Check"
                              style={{ width: 14 }}
                            />{' '}
                            Paid
                          </div>
                        ) : project.purchaseOrderNumber ? (
                          <>
                            <HourglassEmpty style={{ width: 14 }} />
                            Pending approval
                          </>
                        ) : (
                          <div className="flex align-center" ref={hoverRef}>
                            {isHovered ? (
                              <HoverButton onClick={handleAddPOClick}>
                                <Edit />
                                <div>Add purchase order</div>
                              </HoverButton>
                            ) : (
                              <>
                                <HourglassEmpty style={{ width: 14 }} />
                                <div>Pending payment</div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </Grid>
          )}
          {project.purchaseOrderNumber &&
            project.purchaseOrderNumber !== '' && (
              <Grid item xs={12}>
                <ReviewPurchaseOrder />
              </Grid>
            )}
        </Grid>

        <Grid container spacing={0} className="input-row">
          <Grid
            item
            xs={12}
            className="label-item m-t-16 m-b-16"
            style={{ marginBottom: -8 }}
          >
            <div className="flex space-between flex-grow">
              <div className="flex">
                <Bookmark fontSize="small" />
                <h5 className="m-b-0">Service agreement</h5>
              </div>
            </div>
            {project.acceptanceCriteria &&
              project.acceptanceCriteria.length > 0 &&
              project.acceptanceCriteria[project.acceptanceCriteria.length - 1]
                .isSigned && (
                <Button color="primary" onClick={handleShowAddRevision}>
                  <Add className="m-r-5" />
                  Add revision
                </Button>
              )}
          </Grid>

          {showAddRevision && (
            <Grid item xs={12}>
              <Card className="p-l-16 p-r-16 p-b-16 m-b-16">
                <SingleFileInput
                  file={acceptanceCriteria}
                  setFile={setAcceptanceCriteria}
                  contentTop={<>Drag and drop or click to add file</>}
                  contentBottom="Please upload a PDF document."
                  hideStatus={true}
                />
              </Card>
            </Grid>
          )}

          <Grid item xs={12} className="input-item">
            <div style={{ display: 'block', maxWidth: '100%' }}>
              {showAcceptanceCriteria ? (
                <SingleFileInput
                  file={acceptanceCriteria}
                  setFile={setAcceptanceCriteria}
                  contentTop={<>Drag and drop or click to add file</>}
                  contentBottom="Please upload a PDF document."
                  fileType="pdf"
                  project={project}
                />
              ) : (
                project.acceptanceCriteria &&
                project.acceptanceCriteria.length > 0 && (
                  <div className="p-r-16 ">
                    <div className="fs14 m-b-5 flex space-between align-center">
                      <div>
                        {!showAddRevision ? updatedAt : previousUpdatedAt}
                      </div>
                      <div
                        className="align-center flex flex-end gray-text"
                        style={{ flexGrow: 0 }}
                      >
                        {signatureProcessing ? (
                          <div className="flex align-center">
                            <div
                              style={{
                                position: 'relative',
                                width: 14,
                                height: 14,
                              }}
                            >
                              <CircularProgress
                                variant="indeterminate"
                                className="loader"
                                size={14}
                                style={{ left: 0, top: '0px' }}
                              />
                            </div>
                            <div className="m-l-5">Processing</div>
                          </div>
                        ) : project.acceptanceCriteria.length > 0 &&
                          project.acceptanceCriteria[
                            project.acceptanceCriteria.length - 1
                          ].isSigned ? (
                          <div className="signed text-right">
                            <img
                              src={customCircleCheck}
                              alt="Circle Check"
                              style={{ width: 14 }}
                            />{' '}
                            Signed
                          </div>
                        ) : (
                          <>
                            <HourglassEmpty style={{ width: 14 }} />
                            Pending signature
                          </>
                        )}
                      </div>
                    </div>
                    <SingleFileDisplay
                      file={
                        project.acceptanceCriteria[
                          project.acceptanceCriteria.length - 1
                        ]
                      }
                      handleRemoveClick={
                        !documentIsSigned ? handleRemoveClick : null
                      }
                    />
                  </div>
                )
              )}

              {project.acceptanceCriteria &&
                project.acceptanceCriteria.length > 1 && (
                  <div className="flex flex-end">
                    <div
                      color="primary"
                      className="link m-t-6 m-b-6 text-right fs12 m-r-16"
                      onClick={() => setShowRevisionsModal(true)}
                    >
                      See revisions ({project.acceptanceCriteria.length})
                    </div>
                  </div>
                )}
            </div>
          </Grid>
        </Grid>
      </div>

      {(project.status === 'Pending acceptance' ||
        project.status === 'In progress' ||
        project.status === 'Complete') && (
        <ProjectDeliverables
          deliverables={deliverables}
          setDeliverables={setDeliverables}
        />
      )}

      {project.status === 'Submitted for review' && (
        <Button
          variant="contained"
          color="primary"
          onClick={submitHandler}
          disabled={!submitIsReady}
        >
          Submit
        </Button>
      )}

      <RevisionsModal
        setShowRevisionsModal={setShowRevisionsModal}
        showRevisionsModal={showRevisionsModal}
        project={project}
      />

      <ModalPopUp
        title="Attachments"
        setShowModal={setShowAddPODocsModal}
        showModal={showAddPODocsModal}
        variant="medium"
        disableBackdropClick={true}
      >
        <AEAddPODocs setShowAddPODocsModal={setShowAddPODocsModal} />
      </ModalPopUp>
    </form>
  )
}

export default ProjectActionsAdmin
